/**
 * Site-wide messages (`../Steelyard` contracts/global-messages.md): the announcements the customer mobile
 * app shows while Active (not the website). A list screen like `../app-versions/List.tsx` — `ListHeader` + `PagedTable`,
 * its "Add" button in the table's `actions` — with Add/Edit on their own form page (`./Form.tsx`). The
 * row actions are the app's one ⋮ menu (`RowActions`) on the column layout of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx.
 *
 * Gated on `config.administer` by `nav.tsx`'s entry through the root loader. Nothing is deleted:
 * switching a message off retires it.
 */
import { Link, useLoaderData } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { PiPencilSimpleDuotone, PiPlusBold as Plus } from "react-icons/pi";
import { Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { RowActions } from "../../components/table/row-actions";
import { StatusToggle } from "../../components/table/status-toggle";
import { ListHeader } from "../../components/table/list-header";
import { formatDate } from "../../lib/format";
import { listGlobalMessages, messagePath, updateGlobalMessage, type GlobalMessage } from "../../lib/services/globalMessages";

export async function globalMessagesLoader() {
  return { messages: await listGlobalMessages() };
}

export function messageColumns(): ColumnDef<GlobalMessage>[] {
  return [
    { id: "messageNumber", accessorKey: "messageNumber", header: "Number", cell: ({ row }) => <span className="tabular-nums">{row.original.messageNumber}</span> },
    { id: "title", accessorKey: "title", header: "Title" },
    { id: "dismissible", accessorFn: (row) => (row.dismissible ? "Yes" : "No"), header: "Dismissible" },
    {
      id: "active",
      accessorKey: "active",
      header: "Status",
      // No separate toggle route: the switch rides PUT with the row's own other values.
      cell: ({ row }) => (
        <StatusToggle
          name={row.original.title}
          active={row.original.active}
          onChange={(active) => {
            const { id, title, body, dismissible } = row.original;
            return updateGlobalMessage(id, { title, body, dismissible, active });
          }}
        />
      ),
    },
    { id: "updatedAt", accessorKey: "updatedAt", header: "Last updated", cell: ({ row }) => formatDate(row.original.updatedAt) },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => <RowActions name={row.original.title} primary={{ label: "Edit message", icon: PiPencilSimpleDuotone, to: messagePath(row.original) }} />,
    },
  ];
}

export default function GlobalMessagesPage() {
  const { messages } = useLoaderData() as Awaited<ReturnType<typeof globalMessagesLoader>>;

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Global Messages"
        count={messages.length}
        description="Announcements shown in the customer mobile app while Active. A dismissible one can be closed by each reader."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Website" }, { name: "Global Messages" }]}
      />

      {/* A handful of rows: the whole set arrives in one response, paged in memory. */}
      <PagedTable<GlobalMessage>
        actions={
          <Link to="/global-messages/new">
            <Button as="span">
              <Plus aria-hidden className="me-1.5 size-4" />
              Add Message
            </Button>
          </Link>
        }
        result={{ items: messages, page: 1, pageSize: messages.length || 1, totalCount: messages.length }}
        caption="Global messages, newest first"
        columns={messageColumns()}
        getRowId={(row) => row.id}
        empty={<EmptyState title="No global messages yet">Add one to announce something in the customer app.</EmptyState>}
      />
    </div>
  );
}
